import { useEffect, useMemo, useState } from 'react';
import { formatStorage, matchesQuery, PLANS, snippet, subtasksOf, taskProgress, unreadByTask, statusText, type Task } from '@machs/shared';
import { api, userAvatarUrl } from '../lib/api';
import { imageUrl } from '../lib/images';
import { useSync } from '../lib/sync';
import { useSubtaskSort } from '../lib/subtaskSort';
import { usePlan } from '../lib/plan';
import { Icon, type IconName } from '../lib/icons';
import { avatarGradient, initials, taskInitials, listTime } from '../lib/format';
import { applyTheme, storedTheme } from '../lib/theme';
import { Avatar, Logo, Popover } from './ui';
import type { DialogState } from './Shell';

export type ListFilter = 'offen' | 'meine' | 'archiv';

const FILTERS: { id: ListFilter; label: string }[] = [
  { id: 'offen', label: 'Offen' },
  { id: 'meine', label: 'Meine' },
  { id: 'archiv', label: 'Archiv' },
];

const TASK_STATUS = {
  offen: { color: 'var(--brand-a)', icon: 'liste' as IconName },
  erledigt: { color: 'var(--loesung)', icon: 'check' as IconName },
};

/** Anteil erledigter Unteraufgaben und Checklistenpunkte; erledigte Aufgaben sind voll. */
function progressShare(status: string, prog: { subtasks: { done: number; total: number }; checklist: { done: number; total: number } }): number {
  if (status === 'erledigt') return 1;
  const total = prog.subtasks.total + prog.checklist.total;
  return total ? (prog.subtasks.done + prog.checklist.done) / total : 0;
}

interface Props {
  businessId: string | null;
  onBusiness: (id: string) => void;
  projectId: string | null;
  onProject: (id: string | null) => void;
  filter: ListFilter;
  onFilter: (f: ListFilter) => void;
  taskId: string | null;
  onOpenTask: (id: string) => void;
  onTaskDetails: (id: string) => void;
  onBilling: () => void;
  onDialog: (d: DialogState) => void;
  /** Zur Einstiegsseite (Klick auf das Logo) */
  onHome: () => void;
  /** Schmale Leiste statt voller Liste */
  rail: boolean;
  onRail: (v: boolean) => void;
  /** Projekt wird als Board gezeigt: dann trägt der Hauptbereich die Aufgaben */
  boardView: boolean;
  onSettings: () => void;
  onSwitchTeam: () => void;
}

export function Sidebar(p: Props) {
  const { client, me, version } = useSync();
  const sortable = useSubtaskSort();
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());

  const businesses = useMemo(
    () => [...client.data.business.values()].filter((b) => !b.archivedAt).sort((a, b) => Number(b.isDefault) - Number(a.isDefault) || a.name.localeCompare(b.name, 'de')),
    [client, version],
  );
  const projects = useMemo(
    () => [...client.data.project.values()]
      .filter((pr) => !pr.archivedAt && pr.businessId === p.businessId)
      .sort((a, b) => Number(b.isInternal) - Number(a.isInternal) || a.name.localeCompare(b.name, 'de')),
    [client, version, p.businessId],
  );
  const project = p.projectId ? client.data.project.get(p.projectId) ?? null : null;
  const projectIds = new Set(projects.map((pr) => pr.id));
  const unread = useMemo(() => unreadByTask(client, me.user.id), [client, version, me.user.id]);

  const inScope = (t: Task) => (p.projectId ? t.projectId === p.projectId : projectIds.has(t.projectId));
  const allTasks = [...client.data.task.values()];
  const counts = new Map<string, number>();
  for (const t of allTasks) if (t.status !== 'erledigt') counts.set(t.projectId, (counts.get(t.projectId) ?? 0) + 1);

  const tasks = allTasks
    .filter((t) => inScope(t))
    .filter((t) => {
      switch (p.filter) {
        case 'offen': return t.status !== 'erledigt';
        case 'meine': return t.status !== 'erledigt' && (t.assigneeIds.includes(me.user.id) || (t.assigneeIds.length === 0 && t.createdBy === me.user.id));
        case 'archiv': return t.status === 'erledigt';
      }
    })
    .map((t) => ({ t, pinned: client.data.taskUserState.get(`${t.id}:${me.user.id}`)?.pinned ?? false }))
    .sort((a, b) => Number(b.pinned) - Number(a.pinned) || (b.t.lastMessageAt ?? b.t.createdAt).localeCompare(a.t.lastMessageAt ?? a.t.createdAt));

  // Unteraufgaben stehen unter ihrer Hauptaufgabe. Ist die Hauptaufgabe gerade
  // nicht in der Liste (erledigt, anderer Filter), steht die Unteraufgabe für sich.
  const memberName = (id: string) => me.members.find((m) => m.userId === id)?.name.split(' ')[0] ?? '';
  const visible = new Set(tasks.map((x) => x.t.id));
  const nested = (t: Task) => !!t.parentId && visible.has(t.parentId);
  const top = tasks.filter((x) => !nested(x.t));
  const rows = top.flatMap((x) => [
    x,
    ...(collapsed.has(x.t.id) ? [] : subtasksOf(client, x.t.id))
      .map((t) => ({ t, pinned: false })),
  ]);

  // Lokale Volltextsuche: Titel, Nachrichten und Medieninhalte, auch offline

  const business = businesses.find((b) => b.id === p.businessId) ?? null;
  const status = client.status;
  const waiting = status.pendingOps + status.pendingUploads;
  // Abgleich nur noch als kleines Zeichen; der Text steckt im Tooltip
  const syncText = !status.online
    ? `Offline, ${waiting} ${waiting === 1 ? 'Änderung wartet' : 'Änderungen warten'}`
    : status.syncing
      ? 'Wird abgeglichen …'
      : waiting > 0
        ? `${waiting} ${waiting === 1 ? 'Änderung wartet' : 'Änderungen warten'}`
        : status.lastError
          ? `Abgleich fehlgeschlagen: ${status.lastError}`
          : 'Alles abgeglichen';
  const syncTone = !status.online || status.lastError ? 'text-[var(--problem)]' : waiting > 0 ? 'text-[var(--pruefung)]' : 'text-[var(--dim)]';

  // Eingeklappt: nur Symbole und die Aufgaben als Ringe — Zugriff bleibt erhalten
  if (p.rail) {
    return (
      <div className="railbar">
        <button onClick={p.onHome} aria-label="Zur Übersicht" data-tip="Übersicht"><span className="mark" style={{ width: 34, height: 34 }}><Icon name="logo" size={20} strokeWidth={2.6} /></span></button>
        <button className="iconbtn" onClick={() => p.onRail(false)} aria-label="Seitenleiste ausklappen" data-tip="Ausklappen"><Icon name="chev" size={18} /></button>
        <button className="iconbtn" onClick={() => window.dispatchEvent(new Event('machs:search'))} aria-label="Suchen" data-tip="Suchen"><Icon name="search" size={18} /></button>
        <button className="iconbtn" onClick={() => p.onDialog({ kind: 'task', projectId: p.projectId ?? undefined })} aria-label="Neue Aufgabe" data-tip="Neue Aufgabe"><Icon name="plus" size={18} /></button>
        <div className="scroll railtasks">
          {top.map(({ t }) => {
            const ring = TASK_STATUS[t.status === 'erledigt' ? 'erledigt' : 'offen'];
            const share = progressShare(t.status, taskProgress(client, t.id));
            const n = unread.get(t.id) ?? 0;
            const taskProject = client.data.project.get(t.projectId);
            return (
              <button key={t.id} className="railtask" aria-current={p.taskId === t.id} onClick={() => p.onOpenTask(t.id)} aria-label={t.title} data-tip={t.title}>
                <svg viewBox="0 0 42 42" width="42" height="42">
                  <circle cx="21" cy="21" r="18" fill="none" stroke="var(--surface2)" strokeWidth="3" />
                  {share > 0 && <circle cx="21" cy="21" r="18" fill="none" stroke={ring.color} strokeWidth="3" strokeLinecap="round" pathLength={100} strokeDasharray={`${share * 100} 100`} />}
                </svg>
                {/* Kürzel und Farbe gehören der Aufgabe, das Projekt steht als Abzeichen in der Ecke */}
                <span className="railpic">
                  <Avatar name={taskInitials(t.title)} gradient={avatarGradient(t.id)} size={26} square />
                  {taskProject && (
                    <span className="railbadge">
                      <Avatar name={initials(taskProject.name).slice(0, 1)} gradient={taskProject.color ?? avatarGradient(taskProject.id)} src={imageUrl(taskProject.imageMediaId)} size={14} square />
                    </span>
                  )}
                </span>
                {n > 0 && <span className="raildot" />}
              </button>
            );
          })}
        </div>
        {/* Gleiche Höhe wie die Eingabeleiste rechts, damit das Bild auf einer Linie sitzt */}
        <div className="railfoot">
          <button onClick={() => p.onDialog({ kind: 'profile' })} aria-label="Profil" data-tip={me.user.name}>
            <Avatar name={initials(me.user.name)} gradient={avatarGradient(me.user.id)} src={userAvatarUrl(me.user.id, me.user.avatarAt)} size={32} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <div className="min-w-0">
          <button onClick={p.onHome} aria-label="Zur Übersicht" data-tip="Übersicht" data-tip-below="">
            <Logo size={30} claim={businesses.length < 2} />
          </button>
          {businesses.length > 1 && (
            <Popover
              width={280}
              trigger={({ toggle, open }) => (
                <button className="bizswitch" onClick={toggle} aria-haspopup="menu" aria-expanded={open} aria-label="Geschäft wechseln" data-tip="Geschäft wechseln" data-tip-below="">
                  <span className="dot" style={{ background: business?.color ?? 'var(--brand-a)' }} />
                  <span className="truncate">{business?.name ?? 'Geschäft'}</span>
                  <Icon name="down" size={13} />
                </button>
              )}
            >
              {(close) => (
                <>
                  {businesses.map((b) => (
                    <button key={b.id} className="item" aria-selected={b.id === p.businessId} onClick={() => (p.onBusiness(b.id), close())}>
                      <span className="grid h-8 w-8 place-items-center rounded-[10px]" style={{ background: `color-mix(in srgb, ${b.color ?? 'var(--brand-a)'} 20%, transparent)` }}>
                        <span className="h-3 w-3 rounded-full" style={{ background: b.color ?? 'var(--brand-a)' }} />
                      </span>
                      <span className="min-w-0 flex-1 truncate font-medium">{b.name}</span>
                      {b.id === p.businessId && <Icon name="check" size={18} className="text-[var(--brand-a)]" />}
                    </button>
                  ))}
                  <button className="item" onClick={() => (p.onDialog({ kind: 'business', id: p.businessId ?? undefined }), close())}>
                    <span className="grid h-8 w-8 place-items-center rounded-[10px] bg-[var(--surface2)]"><Icon name="gear" size={17} /></span>
                    <span className="font-medium">{business?.name} bearbeiten</span>
                  </button>
                  <button className="item text-[var(--brand-a)]" onClick={() => (p.onDialog({ kind: 'business' }), close())}>
                    <span className="grid h-8 w-8 place-items-center rounded-[10px] bg-[var(--surface2)]"><Icon name="plus" size={18} /></span>
                    <span className="font-semibold">Weiteres Geschäft</span>
                  </button>
                </>
              )}
            </Popover>
          )}
        </div>
        <div className="flex gap-1.5">
          <button className="iconbtn" onClick={() => p.onRail(true)} aria-label="Seitenleiste einklappen" data-tip="Einklappen" data-tip-below="">
            <Icon name="back" size={19} />
          </button>
          <button className="iconbtn" onClick={() => window.dispatchEvent(new Event('machs:search'))} aria-label="Suchen" data-tip="Suchen (⌘K)" data-tip-below="">
            <Icon name="search" size={19} />
          </button>
          <button className="iconbtn" onClick={() => p.onDialog({ kind: 'task', projectId: p.projectId ?? undefined })} aria-label="Neue Aufgabe" data-tip="Neue Aufgabe" data-tip-below="">
            <Icon name="plus" size={19} />
          </button>
        </div>
      </div>


      <div className="filterbar">
        <Popover
          width={328}
          trigger={({ toggle, open }) => (
            <button className="chip projectchip" aria-pressed={!!project} aria-haspopup="menu" aria-expanded={open} onClick={toggle}>
              {project ? (
                <Avatar name={initials(project.name)} gradient={project.color ?? avatarGradient(project.id)} src={imageUrl(project.imageMediaId)} size={18} square />
              ) : (
                <Icon name="folder" size={15} />
              )}
              <span className="max-w-[140px] truncate">{project?.name ?? 'Alle Projekte'}</span>
              {project ? <span className="n">{counts.get(project.id) ?? 0}</span> : null}
              <Icon name="down" size={14} />
            </button>
          )}
        >
          {(close) => (
            <>
              <button className="item" aria-selected={!project} onClick={() => (p.onProject(null), close())}>
                <Avatar name="A" gradient="var(--brand)" size={32} />
                <span className="flex-1 font-medium">Alle Projekte</span>
              </button>
              {projects.map((pr) => (
                <button key={pr.id} className="item" aria-selected={pr.id === project?.id} onClick={() => (p.onProject(pr.id), close())}>
                  <Avatar name={initials(pr.name)} gradient={pr.color ?? avatarGradient(pr.id)} src={imageUrl(pr.imageMediaId)} size={32} square />
                  <span className="min-w-0 flex-1 truncate font-medium">{pr.name}</span>
                  {counts.get(pr.id) ? <span className="rounded-full bg-[var(--surface2)] px-2 text-xs font-semibold text-[var(--muted)]">{counts.get(pr.id)} offen</span> : null}
                  {pr.id === project?.id && <Icon name="check" size={18} className="text-[var(--brand-a)]" />}
                </button>
              ))}
              {project && (
                <button className="item" onClick={() => (p.onDialog({ kind: 'project', id: project.id }), close())}>
                  <span className="grid h-8 w-8 place-items-center rounded-[10px] bg-[var(--surface2)]"><Icon name="gear" size={17} /></span>
                  <span className="font-medium">{project.name} bearbeiten</span>
                </button>
              )}
              {project && (
                <button className="item" onClick={() => (p.onDialog({ kind: 'share', target: { projectId: project.id }, title: project.name }), close())}>
                  <span className="grid h-8 w-8 place-items-center rounded-[10px] bg-[var(--surface2)]"><Icon name="share" size={17} /></span>
                  <span className="font-medium">{project.name} für Externe freigeben</span>
                </button>
              )}
              <button className="item text-[var(--brand-a)]" onClick={() => (p.onDialog({ kind: 'project' }), close())}>
                <span className="grid h-8 w-8 place-items-center rounded-[10px] bg-[var(--surface2)]"><Icon name="plus" size={18} /></span>
                <span className="font-semibold">Projekt anlegen</span>
              </button>
            </>
          )}
        </Popover>
        {/* Nur die Filter scrollen; das Projekt bleibt immer sichtbar */}
        {!p.boardView && (
          <div className="filterscroll">
            {FILTERS.map((f) => (
              <button key={f.id} className="chip" aria-pressed={p.filter === f.id} onClick={() => p.onFilter(f.id)}>{f.label}</button>
            ))}
          </div>
        )}
      </div>



      <div className="scroll flex-1 px-2 pt-1 pb-3">
        {p.boardView ? (
          <div className="px-4 py-10 text-center text-[var(--muted)]">
            <div className="mb-3 flex justify-center text-[var(--dim)]"><Icon name="board" size={30} /></div>
            <p className="m-0 text-[13.5px]">Dieses Projekt wird als Board gezeigt. Die Aufgaben stehen rechts als Karten.</p>
            {project && (
              <button className="chip mt-3" onClick={() => p.onDialog({ kind: 'project', id: project.id })}>
                <Icon name="gear" size={14} />Ansicht ändern
              </button>
            )}
          </div>
        ) : tasks.length === 0 ? (
          <div className="px-4 py-10 text-center text-[var(--muted)]">
            <p className="m-0">{p.filter === 'archiv' ? 'Noch nichts erledigt.' : 'Hier ist gerade nichts offen.'}</p>
            {p.filter !== 'archiv' && (
              <button className="btn btn-ghost mt-3" onClick={() => p.onDialog({ kind: 'task', projectId: p.projectId ?? undefined })}>
                <Icon name="plus" size={18} />Neue Aufgabe
              </button>
            )}
          </div>
        ) : (
          rows.map(({ t, pinned }) => {
            const ring = TASK_STATUS[t.status === 'erledigt' ? 'erledigt' : 'offen'];
            const n = unread.get(t.id) ?? 0;
            const taskProject = client.data.project.get(t.projectId);
            if (nested(t)) {
              return (
                <button key={t.id} {...sortable(t)} title="Zum Sortieren ziehen oder Alt + Pfeiltaste verwenden" className="row subrow" aria-current={p.taskId === t.id} onClick={() => p.onOpenTask(t.id)}>
                  <span className="subdrag" aria-hidden="true">⠿</span>
                  <span className="subring" style={{ color: ring.color, borderColor: ring.color }}>
                    {t.status === 'erledigt' && <Icon name="check" size={12} />}
                  </span>
                  <span className={`min-w-0 flex-1 truncate text-[14px] ${t.status === 'erledigt' ? 'text-[var(--muted)] line-through' : ''}`}>{t.title}</span>
                  {n > 0 && <span className="badge">{n}</span>}
                  {t.assigneeIds[0] && <span className="text-xs text-[var(--dim)]">{memberName(t.assigneeIds[0])}</span>}
                </button>
              );
            }
            const prog = taskProgress(client, t.id);
            const share = progressShare(t.status, prog);
            const parent = t.parentId ? client.data.task.get(t.parentId) : null;
            const closed = t.status === 'erledigt';
            const projectColor = taskProject ? taskProject.color ?? avatarGradient(taskProject.id) : undefined;
            return (
              <div key={t.id} className="rowwrap">
              <div className="rowhead">
              <button className={`row ${closed ? 'closed' : ''}`} aria-current={p.taskId === t.id} onClick={() => p.onOpenTask(t.id)}>
                <span className="statusring">
                  <svg viewBox="0 0 48 48" width="48" height="48">
                    <circle cx="24" cy="24" r="21" fill="none" stroke="var(--surface2)" strokeWidth="3" />
                    {share > 0 && <circle cx="24" cy="24" r="21" fill="none" stroke={ring.color} strokeWidth="3" strokeLinecap="round" pathLength={100} strokeDasharray={`${share * 100} 100`} />}
                  </svg>
                  {/* Kürzel und Farbe der Aufgabe wie in der schmalen Leiste; der Ring zeigt den Fortschritt */}
                  <span className="core"><Avatar name={taskInitials(t.title)} gradient={avatarGradient(t.id)} size={36} /></span>
                  {closed ? (
                    <span className="statusdone"><Icon name="check" size={11} /></span>
                  ) : (
                    !p.projectId && taskProject && (
                      <span className="statusbadge">
                        <Avatar name={initials(taskProject.name).slice(0, 1)} gradient={projectColor!} src={imageUrl(taskProject.imageMediaId)} size={18} square />
                      </span>
                    )
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline justify-between gap-2">
                    <b className="truncate text-[15px] font-semibold">{t.title}</b>
                    {/* Feste Breite, damit das Menü darunter mittig sitzt */}
                    <time className={`w-10 flex-none text-center text-xs ${n ? 'font-semibold text-[var(--brand-b)]' : 'text-[var(--muted)]'}`}>{listTime(t.lastMessageAt ?? t.createdAt)}</time>
                  </span>
                  <span className="mt-0.5 flex items-center justify-between gap-2">
                    <span className="truncate text-[13.5px] text-[var(--muted)]">{t.lastMessagePreview ?? 'Noch keine Nachricht'}</span>
                    {n > 0 ? <span className="badge">{n}</span> : pinned ? <Icon name="pin" size={14} className="flex-none text-[var(--dim)]" /> : null}
                  </span>
                  <span className="mt-1 flex items-center gap-1.5 pr-10">
                    <span className={`tag t-${closed ? 'erledigt' : 'offen'}`}>{statusText(t.status)}</span>
                    {prog.subtasks.total > 0 && (
                      <span className={`progchip pc-sub ${prog.subtasks.done === prog.subtasks.total ? 'full' : ''}`} data-tip={`${prog.subtasks.done} von ${prog.subtasks.total} Unteraufgaben erledigt`}>
                        <Icon name="liste" size={12} />{prog.subtasks.done}/{prog.subtasks.total}
                      </span>
                    )}
                    {prog.checklist.total > 0 && (
                      <span className={`progchip pc-check ${prog.checklist.done === prog.checklist.total ? 'full' : ''}`} data-tip={`${prog.checklist.done} von ${prog.checklist.total} Punkten abgehakt`}>
                        <Icon name="check" size={12} />{prog.checklist.done}/{prog.checklist.total}
                      </span>
                    )}
                    {parent ? (
                      <span className="truncate text-xs text-[var(--dim)]">Teil von {parent.title}</span>
                    ) : (
                      !p.projectId && taskProject && (
                        <span className="flex min-w-0 items-center gap-1.5 text-xs text-[var(--muted)]">
                          <span className="projdot" style={{ background: projectColor }} />
                          <span className="truncate">{taskProject.name}</span>
                        </span>
                      )
                    )}
                  </span>
                </span>
              </button>
              <span className="rowmenu">
                <Popover
                  align="right"
                  width={250}
                  trigger={({ toggle, open }) => (
                    <button className="iconbtn plain" style={{ width: 28, height: 28 }} aria-label={`Aktionen zu ${t.title}`} aria-expanded={open} data-tip="Mehr" data-tip-below="" onClick={(e) => (e.stopPropagation(), toggle())}>
                      <Icon name="more" size={16} />
                    </button>
                  )}
                >
                  {(close) => (
                    <>
                      <button className="item" onClick={() => (void client.saveTask({ id: t.id, projectId: t.projectId, title: t.title, status: t.status === 'erledigt' ? 'offen' : 'erledigt' }), close())}>
                        <Icon name={t.status === 'erledigt' ? 'reset' : 'check'} size={18} />
                        {t.status === 'erledigt' ? 'Aus dem Archiv holen' : 'Als erledigt ablegen'}
                      </button>
                      {!t.parentId && t.status !== 'erledigt' && (
                        <button className="item" onClick={() => (p.onDialog({ kind: 'subtask', parentId: t.id }), close())}>
                          <Icon name="liste" size={18} />Unteraufgabe anlegen
                        </button>
                      )}
                      <button className="item" onClick={() => (void client.setTaskUserState(t.id, { pinned: !pinned }), close())}>
                        <Icon name="pin" size={18} />{pinned ? 'Nicht mehr oben halten' : 'Oben halten'}
                      </button>
                      <button className="item" onClick={() => (p.onDialog({ kind: 'share', target: { taskId: t.id }, title: t.title }), close())}>
                        <Icon name="share" size={18} />Externe einladen
                      </button>
                      <button className="item" onClick={() => (p.onTaskDetails(t.id), close())}>
                        <Icon name="panel" size={18} />Details
                      </button>
                    </>
                  )}
                </Popover>
              </span>
              </div>
              {prog.subtasks.total > 0 && (
                <button className="subtask-toggle" aria-expanded={!collapsed.has(t.id)} aria-label={`Unteraufgaben von ${t.title} ${collapsed.has(t.id) ? 'ausklappen' : 'einklappen'}`}
                  onClick={() => setCollapsed(previous => { const next = new Set(previous); if (next.has(t.id)) next.delete(t.id); else next.add(t.id); return next; })}>
                  <Icon name="chev" size={13} className={collapsed.has(t.id) ? '' : 'rotate-90'} />
                  Unteraufgaben <span>{prog.subtasks.done}/{prog.subtasks.total}</span>
                </button>
              )}
              </div>
            );
          })
        )}
      </div>

      <JoinedList />

      <StorageBar onOpen={p.onBilling} />
      <div className="sidefoot"><div className="profile-settings-row">
        <Popover
          up
          width={280}
          trigger={({ toggle }) => (
            <button className="profilebtn" onClick={toggle} aria-label="Profil und Einstellungen">
              <Avatar name={initials(me.user.name)} gradient={avatarGradient(me.user.id)} src={userAvatarUrl(me.user.id, me.user.avatarAt)} size={34} />
              <span className="min-w-0 flex-1 text-left">
                <b className="block truncate text-[14px]">{me.user.name}</b>
                <small className="block truncate text-[12px] text-[var(--muted)]">{me.tenant.name}</small>
              </span>
              <span className={`flex-none ${syncTone}`} data-tip={syncText} aria-label={syncText} role="status">
                <Icon name={status.online ? 'cloud' : 'cloudOff'} size={17} />
              </span>
            </button>
          )}
        >
          {(close) => (
            <>
              <button className="item" onClick={() => (p.onDialog({ kind: 'profile' }), close())}>
                <Avatar name={initials(me.user.name)} gradient={avatarGradient(me.user.id)} src={userAvatarUrl(me.user.id, me.user.avatarAt)} size={36} />
                <span className="min-w-0 flex-1">
                  <b className="block truncate">{me.user.name}</b>
                  <small className="block truncate text-xs text-[var(--muted)]">Profil bearbeiten</small>
                </span>
              </button>
              <button className="item" onClick={() => (applyTheme(storedTheme() === 'hell' ? 'dunkel' : 'hell'), close())}>
                <Icon name={storedTheme() === 'hell' ? 'moon' : 'sun'} size={18} />{storedTheme() === 'hell' ? 'Dunkles Design' : 'Helles Design'}
              </button>
              <button className="item" onClick={() => (p.onDialog({ kind: 'notify' }), close())}><Icon name="bell" size={18} />Benachrichtigungen</button>
              <button className="item" onClick={() => (p.onSettings(), close())}><Icon name="gear" size={18} />Einstellungen</button>
              {me.teams.filter((t) => t.id !== me.tenant.id).map((t) => (
                <button key={t.id} className="item" onClick={async () => {
                  close();
                  const r = await api<{ token: string }>('/auth/switch', { method: 'POST', json: { tenantId: t.id } });
                  p.onSwitchTeam();
                }}>
                  <Icon name="user" size={18} />Zu {t.name} wechseln
                </button>
              ))}
              <button className="item" onClick={() => window.dispatchEvent(new Event('machs:logout'))}><Icon name="logout" size={18} />Abmelden</button>
            </>
          )}
        </Popover>
        <button className="iconbtn" aria-label="Einstellungen öffnen" onClick={p.onSettings}><Icon name="gear" size={20} /></button>
      </div></div>

    </>
  );
}

interface Joined {
  shareId: string;
  team: string;
  kind: 'aufgabe' | 'projekt';
  title: string;
  active: boolean;
}

/** Freigaben aus fremden Teams, denen dieses Konto beigetreten ist. */
function JoinedList() {
  const [list, setList] = useState<Joined[]>([]);
  useEffect(() => {
    api<{ joined: Joined[] }>('/shares/joined')
      .then((r) => setList(r.joined.filter((j) => j.active)))
      .catch(() => {});
  }, []);
  if (list.length === 0) return null;
  return (
    <div className="border-t border-[var(--line)] px-2 py-2">
      <div className="px-3 pb-1 text-xs font-semibold text-[var(--muted)]">Mit mir geteilt</div>
      {list.map((j) => (
        <a key={j.shareId} href={`/geteilt/${j.shareId}`} className="row !py-1.5 no-underline" style={{ color: 'inherit' }}>
          <span className="grid h-9 w-9 flex-none place-items-center rounded-xl bg-[var(--surface2)] text-[var(--brand-b)]"><Icon name="share" size={17} /></span>
          <span className="min-w-0 flex-1">
            <b className="block truncate text-[14px]">{j.title}</b>
            <span className="block truncate text-xs text-[var(--muted)]">{j.kind === 'projekt' ? 'Projekt' : 'Aufgabe'} von {j.team}</span>
          </span>
        </a>
      ))}
    </div>
  );
}

/**
 * Dezente Anzeige unten links: belegter Speicher im Verhältnis zum Tarif,
 * im Testzeitraum dazu die verbleibenden Tage. Ein Klick führt zu Tarif und Nutzung.
 */
function StorageBar({ onOpen }: { onOpen: () => void }) {
  const { info } = usePlan();
  if (!info || info.limits.storageMb === null) return null;
  const used = info.usage.storageMb;
  const max = info.limits.storageMb;
  const share = Math.min(1, used / max);
  const days = info.trial && info.trialEndsAt ? Math.max(0, Math.ceil((new Date(info.trialEndsAt).getTime() - Date.now()) / 86_400_000)) : null;
  return (
    <button
      className={`storagebar ${share >= 1 ? 'full' : share >= 0.8 ? 'warn' : ''}`}
      onClick={onOpen}
      data-tip={`Tarif ${PLANS[info.plan].name}${days !== null ? ' (Test)' : ''}: Tarif und Nutzung ansehen`}
    >
      <span className="row">
        <span>Speicher {formatStorage(used)} von {formatStorage(max)}</span>
        {days !== null && <span>{PLANS[info.plan].name}-Test · {days === 1 ? '1 Tag' : `${days} Tage`}</span>}
      </span>
      <span className="track"><span className="fill" style={{ width: `${Math.max(share * 100, used > 0 ? 2 : 0)}%` }} /></span>
    </button>
  );
}
